/**
 * Organization Selector Component
 * Dropdown for switching between organizations
 * - Shows org name only for single-org users
 * - Full dropdown with API search for multi-org users
 * - Mobile-friendly bottom sheet on small screens
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { api, type Organization } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

interface OrganizationSelectorProps {
  /** Total count of organizations user has access to */
  totalOrganizations: number;
  /** Initial organization to display (before search) */
  initialOrganization?: Organization | null;
}

export function OrganizationSelector({ totalOrganizations, initialOrganization }: OrganizationSelectorProps) {
  const { currentOrganizationId, setCurrentOrganizationId, currentOrganization } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Organization[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get the currently selected organization to display
  const selectedOrg = currentOrganization || initialOrganization;

  // Load initial results when dropdown opens
  const loadInitialResults = useCallback(async () => {
    setIsSearching(true);
    try {
      const result = await api.getOrganizations({ limit: 20 });
      if (result.success && result.data) {
        setSearchResults(result.data.organizations);
      }
    } catch (error) {
      console.error('Failed to load organizations:', error);
    } finally {
      setIsSearching(false);
      setHasSearched(true);
    }
  }, []);

  // Search organizations via API with debounce
  const searchOrganizations = useCallback(async (query: string) => {
    setIsSearching(true);
    try {
      const result = await api.getOrganizations({
        search: query || undefined,
        limit: 20,
      });
      if (result.success && result.data) {
        setSearchResults(result.data.organizations);
      }
    } catch (error) {
      console.error('Failed to search organizations:', error);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Handle search input change with debounce
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce API call
    searchTimeoutRef.current = setTimeout(() => {
      searchOrganizations(value);
    }, 300);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
        setHasSearched(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load initial results when dropdown opens
  useEffect(() => {
    if (isOpen && !hasSearched) {
      loadInitialResults();
    }
  }, [isOpen, hasSearched, loadInitialResults]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Small delay to ensure dropdown is rendered
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setSearchQuery('');
        setHasSearched(false);
      }
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSelectOrganization = (org: Organization) => {
    setCurrentOrganizationId(org.id);
    setIsOpen(false);
    setSearchQuery('');
    setHasSearched(false);

    // Dispatch event for pages to refresh
    window.dispatchEvent(new CustomEvent('organizationChanged', { detail: org }));
  };

  // Single org - just show name (no dropdown)
  if (totalOrganizations <= 1) {
    return (
      <div className="px-4 py-3 border-b">
        <label className="block text-xs font-medium text-gray-500 mb-1">Organization</label>
        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
          <span className="text-lg">🏢</span>
          <span className="text-sm font-medium text-gray-700 truncate">
            {selectedOrg?.name || 'No Organization'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-3 border-b" ref={dropdownRef}>
      <label className="block text-xs font-medium text-gray-500 mb-1">Organization</label>

      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-white border rounded-lg hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-lg flex-shrink-0">🏢</span>
          <span className="text-sm font-medium text-gray-700 truncate">
            {selectedOrg?.name || 'Select Organization'}
          </span>
        </div>
        <svg
          className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Mobile backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => {
              setIsOpen(false);
              setSearchQuery('');
              setHasSearched(false);
            }}
          />

          {/* Dropdown content - Fixed positioning for proper rendering */}
          <div className="fixed inset-x-0 bottom-0 z-[60] lg:left-4 lg:right-auto lg:bottom-auto lg:top-[140px] lg:w-56">
            <div className="bg-white rounded-t-2xl lg:rounded-lg shadow-xl lg:shadow-lg border lg:border-gray-200 max-h-[70vh] lg:max-h-96 overflow-hidden">
              {/* Mobile header */}
              <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b">
                <h3 className="font-semibold text-gray-900">Select Organization</h3>
                <button
                  onClick={() => {
                    setIsOpen(false);
                    setSearchQuery('');
                    setHasSearched(false);
                  }}
                  className="p-1 rounded-full hover:bg-gray-100"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Search - always visible */}
              <div className="p-3 border-b">
                <div className="relative">
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="Search organizations..."
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {isSearching && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <svg className="animate-spin w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  {totalOrganizations} organizations available
                </div>
              </div>

              {/* Organization list */}
              <ul className="overflow-y-auto max-h-[calc(70vh-160px)] lg:max-h-72 py-1">
                {!hasSearched ? (
                  <li className="px-4 py-6 text-center text-gray-500 text-sm">
                    Loading...
                  </li>
                ) : searchResults.length === 0 ? (
                  <li className="px-4 py-6 text-center text-gray-500 text-sm">
                    {searchQuery ? 'No organizations found' : 'No organizations available'}
                  </li>
                ) : (
                  searchResults.map(org => (
                    <li key={org.id}>
                      <button
                        onClick={() => handleSelectOrganization(org)}
                        className={`w-full flex items-center justify-between px-4 py-3 lg:py-2.5 hover:bg-gray-50 transition-colors ${
                          currentOrganizationId === org.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-lg flex-shrink-0">🏢</span>
                          <div className="min-w-0 text-left">
                            <div className={`text-sm font-medium truncate ${
                              currentOrganizationId === org.id ? 'text-blue-600' : 'text-gray-900'
                            }`}>
                              {org.name}
                            </div>
                            <div className="text-xs text-gray-500">{org.memberCount} members</div>
                          </div>
                        </div>
                        {currentOrganizationId === org.id && (
                          <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
